import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { useServerTable } from '@/hooks/use-server-table';
import {
  useDepartmentOptions,
  useDesignationOptions,
  useEmployeesByUserIds,
} from '@/modules/shared/hr-lookups';
import {
  DEFAULT_PM_REVIEWS_ORDER,
  EMPTY_REVIEW_FILTERS,
  ReviewFilters,
  ReviewFilterValue,
  useCurrentUserId,
} from '@/modules/performance/hr-review-core';
import { createPeerRequestsTableColumns } from '../../components/peer-requests-table/peer-requests-table-columns';
import { ForwardPeerReviewDialog } from '../../components/forward-peer-review-dialog/forward-peer-review-dialog';
import { SelectPeersDialog } from '../../components/select-peers-dialog/select-peers-dialog';
import { useGetPeerRequests } from '../../hooks/use-peer-requests';
import { PeerRequest } from '../../types/peer-request.types';

/** My Reviews ? Requests: the peer lists I'm asked to choose (or forward), with the review filters. */
export const PeerRequestsList = () => {
  const { t } = useTranslation();
  const managerId = useCurrentUserId();
  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_PM_REVIEWS_ORDER,
  });
  const [filters, setFilters] = useState<ReviewFilterValue>(EMPTY_REVIEW_FILTERS);
  const [selectTarget, setSelectTarget] = useState<PeerRequest | null>(null);
  const [forwardTarget, setForwardTarget] = useState<PeerRequest | null>(null);

  const { data: departments = [] } = useDepartmentOptions();
  const { data: designations = [] } = useDesignationOptions();
  const departmentNames = useMemo(
    () => new Map(departments.map((option) => [option.ItemId, option.Title])),
    [departments]
  );
  const designationNames = useMemo(
    () => new Map(designations.map((option) => [option.ItemId, option.Title])),
    [designations]
  );

  const { data, isLoading, error } = useGetPeerRequests(managerId, filters, {
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    order,
    columnWhere,
  });
  const { byUserId: employeesByUserId } = useEmployeesByUserIds(
    (data?.items ?? []).map((request) => request.review.ReviewForId)
  );
  const emailOf = (userId: string) => employeesByUserId.get(userId)?.Email;

  const handleFiltersChange = (next: ReviewFilterValue) => {
    setFilters(next);
    resetPage();
  };

  const columns = createPeerRequestsTableColumns({
    t,
    departmentNames,
    designationNames,
    emailOf,
    onSelectPeers: setSelectTarget,
    onForward: setForwardTarget,
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <ReviewFilters value={filters} onChange={handleFiltersChange} lockSingleDepartment={false} />

      <AdvanceDataTable
        {...tableProps}
        data={data?.items ?? []}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount: data?.totalCount ?? 0,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <SelectPeersDialog
        open={Boolean(selectTarget)}
        onOpenChange={(open) => !open && setSelectTarget(null)}
        request={selectTarget}
        managerId={managerId}
      />
      <ForwardPeerReviewDialog
        open={Boolean(forwardTarget)}
        onOpenChange={(open) => !open && setForwardTarget(null)}
        request={forwardTarget}
        managerId={managerId}
      />
    </div>
  );
};
